import type { ConversionJob, DownloadKind, StaticModelPage } from './types';

export class ConverterError extends Error {
  constructor(message: string, public readonly status?: number) { super(message); }
}

export function validateSource(value: string): string {
  const text = value.trim();
  let url: URL;
  try { url = new URL(text); } catch { throw new Error('Introduce una URL válida de una ficha Indexado V5.'); }
  if (url.protocol !== 'https:' || url.hostname !== 'front.indexado.production.alquimio.cloud' ||
    url.port || url.username || url.password || url.pathname !== '/spec' || url.hash) {
    throw new Error('Usa una URL HTTPS de front.indexado.production.alquimio.cloud/spec.');
  }
  for (const key of ['modelId', 'lang', 'version']) {
    if (url.searchParams.getAll(key).length !== 1) throw new Error(`La URL debe contener un único parámetro ${key}.`);
  }
  if (!/^[a-f0-9]{24}$/i.test(url.searchParams.get('modelId') ?? '') ||
    !/^[a-z]{2}[A-Z]{2}$/.test(url.searchParams.get('lang') ?? '') || url.searchParams.get('version') !== 'V5') {
    throw new Error('Comprueba modelId, lang (por ejemplo esCL) y version=V5 en la URL.');
  }
  url.searchParams.forEach((_, key) => {
    if (!['modelId', 'lang', 'version', 'forceRefresh'].includes(key)) throw new Error(`El parámetro ${key} no está soportado.`);
  });
  const refresh = url.searchParams.getAll('forceRefresh');
  if (refresh.length > 1 || (refresh.length === 1 && !/^(true|false)$/i.test(refresh[0]))) {
    throw new Error('forceRefresh debe ser true o false.');
  }
  return text;
}

export function createStaticModelsService(baseUrl: string, token: string, request: typeof fetch = fetch) {
  const base = baseUrl.replace(/\/+$/, '');
  async function call<T>(path: string, options: RequestInit = {}): Promise<T> {
    let response: Response;
    try {
      response = await request(base + '/api/v1' + path, {
        ...options,
        headers: { Authorization: `Bearer ${token}`, ...options.headers },
        signal: options.signal ?? AbortSignal.timeout(30000),
        credentials: 'omit',
        redirect: 'error',
      });
    } catch (cause) {
      if (options.signal?.aborted) throw cause;
      throw new ConverterError('No se pudo conectar con el conversor. Comprueba que esté disponible y vuelve a intentarlo.');
    }
    const body = await response.json().catch(() => null);
    if (!response.ok) {
      const detail = typeof body?.detail === 'string' ? body.detail : '';
      throw new ConverterError(response.status === 401 ? 'Tu sesión no es válida para el conversor. Vuelve a iniciar sesión.' :
        detail || (response.status === 429 ? 'La cola está llena. Inténtalo más tarde.' : 'No se pudo completar la operación.'), response.status);
    }
    return body as T;
  }
  return {
    list: (cursor: string | null, signal?: AbortSignal) => {
      const query = new URLSearchParams({ limit: '20' });
      if (cursor) query.set('cursor', cursor);
      return call<StaticModelPage>('/jobs?' + query.toString(), { signal });
    },
    submit: async (url: string, idempotencyKey: string) => call<ConversionJob>('/staticize', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Idempotency-Key': idempotencyKey },
      body: JSON.stringify({ url: validateSource(url), profile: 'current' }),
    }),
    download: async (jobId: string, kind: DownloadKind): Promise<string> => {
      if (!/^[a-f0-9]{32}$/.test(jobId)) throw new Error('Identificador de solicitud inválido.');
      // Cada clic consulta el estado para renovar el enlace temporal.
      const job = await call<ConversionJob>('/jobs/' + jobId);
      const href = job.result?.[kind];
      if (job.status !== 'completed' || !href) throw new Error('El archivo todavía no está disponible o ya expiró.');
      const target = new URL(href);
      if (!['http:', 'https:'].includes(target.protocol)) throw new Error('El enlace de descarga no es válido.');
      return target.href;
    },
  };
}
